/**
 * Ability copy — the one place an ability is turned into words.
 *
 * Everything here is derived from `CHARACTERS`, which is the same table the
 * SERVER resolves hits from. That is the whole point: a tooltip that says
 * "9 damage, 2.6m cone" and a sim that deals 8 in a 3m cone is worse than no
 * tooltip, because the player then makes decisions on it. Nothing below is
 * authored twice — if a number moves in the roster table, this moves with it.
 *
 * `describeShape` was living in the gym panel. Two copies of "how do you say a
 * hit shape out loud" is exactly the drift this file exists to stop, so the gym
 * now imports it from here.
 */

import { CHARACTERS } from '@chaos/protocol';
import { DASH_COOLDOWN_MS, DASH_DURATION_MS } from '@chaos/sim';

/** How the ability reaches, in one short phrase. */
export function describeShape(ability) {
  const hit = ability.hit;
  switch (hit.kind) {
    case 'melee_cone':
      return `cone ${hit.range}m / ${hit.halfAngleDeg * 2}°`;
    case 'sphere':
      return `sphere r${hit.radius}m`;
    case 'dash_line':
      return `line ${ability.selfMove || hit.length}m x ${hit.width}m`;
    case 'projectile':
      return `projectile ${hit.speed}u/s · splash ${hit.radius}m`;
    case 'ground_zone':
      return `ground r${hit.radius}m · ${hit.durationMs / 1000}s`;
    default:
      return 'self';
  }
}

/**
 * The stat line: damage, cooldown, wind-up.
 *
 * Wind-up is included because it is the number that decides whether an ability
 * can be REACTED to, and it is invisible everywhere else in the UI. A 700ms
 * wind-up is a completely different weapon from a 90ms one at the same damage,
 * and a player who does not know that will keep walking into Supercycle.
 */
export function abilityStats(ability) {
  const out = [];
  if (ability.damage > 0) out.push(`${ability.damage} dmg`);
  if (ability.cooldownMs > 0) out.push(`${(ability.cooldownMs / 1000).toFixed(ability.cooldownMs % 1000 ? 1 : 0)}s cd`);
  else out.push('charge');
  if (ability.windupMs > 0) out.push(`${ability.windupMs}ms wind-up`);
  return out;
}

/**
 * Everything the UI needs to describe one slot, or null if there is no such
 * ability. `dash` is deliberately not in the roster table — it is universal and
 * the sim files its cooldown under the ability id "dash" — so it is described
 * here rather than pretended into `CHARACTERS`.
 */
export function abilityCopy(characterId, slot) {
  if (slot === 'dash') {
    // READ from the sim, not retyped. Dash is not in the roster table because
    // it is universal, and this file's header promises nothing is authored
    // twice — it was, until now: the i-frame window and the cooldown were both
    // literals here, and the cooldown was a PARAMETER so every caller could
    // supply its own answer. A dash tuning pass would have left the tooltip and
    // the character sheet promising the old numbers to someone betting on them.
    return {
      name: 'Dash',
      shape: `self · ${DASH_DURATION_MS}ms of i-frames`,
      stats: [`${(DASH_COOLDOWN_MS / 1000).toFixed(0)}s cd`, 'invulnerable'],
      description:
        'A short burst along your movement, not your aim. The i-frames make it a dodge as much as a gap-closer.',
    };
  }
  const def = CHARACTERS[characterId] ?? CHARACTERS.volt;
  const ability = def.abilities[slot];
  if (!ability) return null;
  return {
    name: ability.name,
    shape: describeShape(ability),
    stats: abilityStats(ability),
    description: ability.description,
  };
}
